import type {
  AuthPinCredentials,
  IAuthPinRepository,
} from '@/domain/repositories/IAuthPinRepository';
import type { IActiveSessionRepository } from '@/domain/repositories/IActiveSessionRepository';
import type { DeviceSession } from '@/domain/entities/DeviceSession';
import { toActiveSessionSnapshot } from '@/domain/entities/ActiveSessionSnapshot';

export type AuthPinResult =
  | { success: true; session: DeviceSession }
  | { success: false; error: string };

/**
 * Use case responsável por autenticar um dispositivo via PIN de evento.
 * Encapsula o tratamento de erro da chamada ao repositório — a camada de
 * apresentação nunca lida com exceções de rede diretamente.
 *
 * Desde a HUB-236 (ADR-003, Adendo 2026-07-25 — Correção 2), toda
 * autenticação bem-sucedida também espelha a sessão em IndexedDB
 * (`activeSession`), único jeito do Service Worker autenticar a
 * sincronização em segundo plano (Background Sync roda mesmo com a aba
 * fechada, sem acesso ao estado em memória do React).
 */
export class AuthPinUseCase {
  constructor(
    private readonly repository: IAuthPinRepository,
    private readonly activeSessionRepository: IActiveSessionRepository
  ) {}

  async execute(credentials: AuthPinCredentials): Promise<AuthPinResult> {
    try {
      const session = await this.repository.authenticate(credentials);
      await this.mirrorActiveSession(session);
      return { success: true, session };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro desconhecido ao validar PIN.';
      return { success: false, error: message };
    }
  }

  /** Falha ao espelhar a sessão não invalida um PIN já validado pelo
   * backend — é logada estruturada (mesma filosofia do CA-03: sem
   * exposição em UI). O pior efeito é a sincronização em segundo plano não
   * funcionar até a próxima autenticação; a fila local não é afetada. */
  private async mirrorActiveSession(session: DeviceSession): Promise<void> {
    try {
      await this.activeSessionRepository.save(toActiveSessionSnapshot(session));
    } catch (error) {
      console.error('[AuthPinUseCase] Falha ao espelhar sessão ativa em IndexedDB.', {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }
}
